import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseHttpService } from '../http/base.http.service';
import { ApiRouterService } from '../http/api.router';
import { IServiceInfoDetail, ITown } from 'src/app/models';
import { objectToSnake } from 'ts-case-convert';
import { ServiceInfoApiRoutes } from './service-info-api.routes';

@Injectable({
  providedIn: 'root',
})
export class ServiceInfoHttpService extends BaseHttpService {
  private baseURL = `service-infos`;

  constructor(
    @Inject(HttpClient) httpClient: HttpClient,
    private routerService: ApiRouterService,
  ) {
    super(httpClient);
  }

  public getServiceInfos(size: number, page: number, towns?: ITown[]) {
    const townIds = towns && towns.map((town: ITown) => town.id);
    let url = this.routerService.mount(`${this.baseURL}?page=${page}&size=${size}`);
    if (townIds && townIds.length > 0) url += `&towns=${townIds}`;
    return this.read(url);
  }

  public getServiceInfosSearch(size: number, page: number, towns?: ITown[], searchValue?: string) {
    const townIds = towns && towns.map((town: ITown) => town.id);
    let url = this.routerService.mount(`${this.baseURL}/filter?page=${page}&size=${size}`);
    if (townIds && townIds.length > 0) url += `&towns=${townIds}`;
    if (searchValue) url += `&search=${encodeURIComponent(searchValue)}`;
    return this.read(url);
  }

  public filterServiceInfo(page: number, size: number, clientIds?: number[], searchValue?: string) {
    let url = this.routerService.mount(`${this.baseURL}/filter?page=${page}&size=${size}`);
    if (clientIds && clientIds.length > 0) url += `&clients=${clientIds}`;
    if (searchValue) url += `&search=${encodeURIComponent(searchValue)}`;
    return this.read(url);
  }

  public getServiceInfo(serviceId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${serviceId}`);
    return this.read(url);
  }

  public getServiceInfosByTowns(townIds: number[], page?: number, size?: number) {
    let url = this.routerService.mount(`${this.baseURL}/${ServiceInfoApiRoutes.TOWNS}?town_ids=${townIds}`);
    url += page ? `&page=${page}` : '';
    url += size ? `&size=${size}` : '';
    return this.read(url);
  }

  public getServiceInfoTerrains(serviceInfoId: number) {
    let url = this.routerService.mount(`${this.baseURL}/${serviceInfoId}/terrains`);
    return this.read(url);
  }

  public createService(newService: IServiceInfoDetail) {
    const url = this.routerService.mount(`${this.baseURL}`);
    const body = objectToSnake(
      {
        name: newService.name,
        tag: newService.tag,
        color: newService.color,
        forwardDirection: newService.forwardDirection,
        backwardsDirection: newService.backwardsDirection,
        showTheoreticalTimes: newService.showTheoreticalTimes,
        showRealTimes: newService.showRealTimes,
        sortCriteria: newService.sortCriteria,
        serviceType: newService.serviceType,
        bookingTimePicker: newService.bookingTimePicker,
        roundTrips: newService.roundTrips,
        // dependentUsers: newService.dependentUsers,
        addressSearch: newService.addressSearch,
        gtfsId: newService.gtfsId,
        extKey: newService.extKey,
        towns: newService.towns.filter((it) => it.id == newService.townId).map((town: any) => {
          return { id: town.id, name: town.name}
        }),
        clients: newService.clients.filter((it) => it.id == newService.clientId).map((client: any) => {
          return { id: client.id, name: client.name}
        })
      }
    );
    return this.create(body, url);
  }

  public updateService(service: IServiceInfoDetail) {
    const url = this.routerService.mount(`${this.baseURL}/${service.id}`);
    const body = objectToSnake(
      {
        id: service.id,
        name: service.name,
        tag: service.tag,
        color: service.color,
        forwardDirection: service.forwardDirection,
        backwardsDirection: service.backwardsDirection,
        showTheoreticalTimes: service.showTheoreticalTimes,
        showRealTimes: service.showRealTimes,
        sortCriteria: service.sortCriteria,
        serviceType: service.serviceType,
        bookingTimePicker: service.bookingTimePicker,
        roundTrips: service.roundTrips,
        // dependentUsers: service.dependentUsers,
        addressSearch: service.addressSearch,
        gtfsId: service.gtfsId,
        extKey: service.extKey,
        routes: service.routes
        // towns: service.towns,
        // clients: service.clients
        // townId: service.towns![0].id,
        // clientId: service.clients![0].id
      }
    );
    return this.update(body, url);
  }

  public exportServiceFromGtfs(serviceInfoIds: number[], routeIds?: number[]) {
    let url = this.routerService.mount(`${this.baseURL}/${ServiceInfoApiRoutes.EXPORT_GTFS}?ids=${serviceInfoIds}`);
    routeIds && (url += `&route_ids=${routeIds}`);
    return this.createText({}, url);
  }

  public downloadGtfs(endpoint: string) {
    let url = this.routerService.mount(`${this.baseURL.replace('service-infos', endpoint.substring(1))}`);
    return this.readZip(url);
  }

  public deleteService(serviceId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${serviceId}`);
    return this.delete(url);
  }

  public getPossibleRoutes(serviceId: number, bookings: number[]) {
    let url = this.routerService.mount(`${this.baseURL}/${serviceId}/routes?bookingIds=${bookings[0]}`);
    return this.read(url);
  }

  public getAvailablities(serviceId: number, page?: number, size?: number) {
    let url = this.routerService.mount(`${this.baseURL}/${serviceId}/availabilities?page=${page}&size=${size}`);
    return this.read(url);
  }

  public updateSetupsConfig(serviceInfoId: number, config: any, options?: {
    deleteAvailabilities?: boolean;
    deleteComment?: string;
    sendNotification?: boolean;
    refund?: boolean;
    cancelRelated?: boolean;
    fullRefund?: boolean;
  }) {
    let url = this.routerService.mount(`${this.baseURL}/${serviceInfoId}/setups/config`);

    if (options) {
      const params: string[] = [];
      if (options.deleteAvailabilities !== undefined) params.push(`deleteAvailabilities=${options.deleteAvailabilities}`);
      if (options.deleteComment) params.push(`deleteComment=${encodeURIComponent(options.deleteComment)}`);
      if (options.sendNotification !== undefined) params.push(`sendNotification=${options.sendNotification}`);
      if (options.refund !== undefined) params.push(`refund=${options.refund}`);
      if (options.cancelRelated !== undefined) params.push(`cancelRelated=${options.cancelRelated}`);
      if (options.fullRefund !== undefined) params.push(`fullRefund=${options.fullRefund}`);
      if (params.length > 0) url += `?${params.join('&')}`;
    }

    const body = objectToSnake(config);
    return this.update(body, url);
  }
}
